import { presentInsuredDetailsAtom, presentPayWallAtom } from 'app/atoms/ui';
import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { InsuredBadge } from 'app/design/icons/insured-badge';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text, Text as InlineText } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { useUserCanBeInsured, useUserCurrency, useUserIsInsured } from 'app/network/user';
import { isLoanDiscountActive } from 'app/utils/loan-discount';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ScrollView } from 'react-native-gesture-handler';
import React from 'react';
import { Event, track } from 'app/lib/analytics';
import { Separator } from 'app/design/primitives/text-divider';
import { PlanRecurrenceEnum } from 'app/schemas/plan';
import { useReferralPrizeAvailable } from 'app/network/promotions';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCheckbox, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { hasExpiredPlanAtom, userPlanAtom } from 'app/atoms';
import { isFreeTrialPlanCode } from 'app/network/plan';
import type { CurrencyType } from 'app/schemas/user';
import { useLoanDiscount } from 'app/hooks/use-loan-discount';
const theme = createDSTheme();

function Paywall() {
  const { t } = useTranslation(['paywall', 'payment']);
  const navigation = useNavigation();
  const setModalVisible = useSetAtom(presentPayWallAtom);
  const hasExpiredPlan = useAtomValue(hasExpiredPlanAtom);
  const currentPlan = useAtomValue(userPlanAtom)?.[0];
  const setInsuredDetails = useSetAtom(presentInsuredDetailsAtom);
  const userCurrency = useUserCurrency();
  const { discountDeadline: loanDiscountDeadline } = useLoanDiscount();
  const hasExpiredRealPlan = hasExpiredPlan && Boolean(currentPlan && !isFreeTrialPlanCode(currentPlan.code.code));
  const hasDiscount = !hasExpiredRealPlan && isLoanDiscountActive(loanDiscountDeadline);

  const closeModal = (action: 'cancel' | 'subscribe') => {
    setModalVisible(false);
    track(Event.PaywallClosed, {
      action,
    });
    if (action === 'cancel') {
      navigation.goBack();
    }
  };

  const goToPayment = () => {
    closeModal('subscribe');
    navigation.replace('payment', {
      promoCode: hasDiscount ? 'PARTAGE25' : undefined,
    });
  };

  const heroKey = hasExpiredRealPlan ? 'expired' : 'standard';

  return (
    <SafeAreaView edges={['top', 'bottom']} style={{ backgroundColor: theme.colors['surface.secondary'], flex: 1 }}>
      <Box backgroundColor="surface.secondary" flex={1}>
        <ScrollView
          automaticallyAdjustContentInsets={false}
          horizontal={false}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ flexGrow: 1 }}
          style={{ flex: 1 }}
        >
          <Column flex={1} gap="spacing.general.x6" padding="spacing.general.x4">
            <Pressable onPress={() => closeModal('cancel')}>
              <IconX color={theme.colors['icon.primary']} size={30} strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
            <Column alignItems="center" gap="spacing.general.x4">
              <Text color="text.primary" fontSize={40} fontWeight="700" lineHeight={44} textAlign="center">
                {t(`redesign.${heroKey}.title`)}
              </Text>
              <Text color="text.primary" fontSize={18} lineHeight={22} textAlign="center">
                {t(`redesign.${heroKey}.subtitle`)}
              </Text>
            </Column>

            <Column gap="spacing.general.x1">
              <AnnualPlan hasDiscount={hasDiscount} userCurrency={userCurrency} />
              <IncludedBenefits onShowInsurance={() => setInsuredDetails({ insured: true })} />
            </Column>
          </Column>
        </ScrollView>

        <Column gap="spacing.general.x2" paddingBottom="spacing.general.x6" paddingHorizontal="spacing.general.x4">
          <Text color="text.secondary" fontSize={14} lineHeight={22} textAlign="center">
            {t('redesign.cancelAnytime')}
          </Text>
          <Button onPress={goToPayment} variant="primary">
            {`${t('redesign.subscribe')} →`}
          </Button>
          <Button onPress={() => closeModal('cancel')} variant="secondary">
            {t('redesign.noThanks')}
          </Button>
        </Column>
      </Box>
    </SafeAreaView>
  );
}
function AnnualPlan({ hasDiscount, userCurrency }: { hasDiscount: boolean; userCurrency: CurrencyType }) {
  const { t } = useTranslation(['paywall', 'payment']);
  return (
    <Row
      alignItems="center"
      backgroundColor="surface.brand.secondary-5.subtle"
      borderRadius="radius.lg"
      justifyContent="space-between"
      padding="spacing.general.x4"
    >
      <Column flex={1} gap="spacing.general.x1">
        <Row alignItems="center" gap="spacing.general.x2">
          <PartagePlusBadge />
          {hasDiscount ? (
            <Box
              backgroundColor="surface.primary-neutral"
              borderRadius="radius.full"
              paddingHorizontal="spacing.general.x4"
              paddingVertical="spacing.general.x1"
            >
              <Text color="text.primary" fontSize={12} fontWeight="700">
                {t('loanDiscount.percentage', { ns: 'payment' })}
              </Text>
            </Box>
          ) : null}
        </Row>
        <Row alignItems="center" flexWrap="wrap" gap="spacing.general.x1">
          {hasDiscount ? (
            <Text color="text.secondary" fontSize={14} lineHeight={22} textDecorationLine="line-through">
              {t(`price.${PlanRecurrenceEnum.yearly}.${userCurrency}`, { ns: 'payment' })}/
              {t(`period.${PlanRecurrenceEnum.yearly}`, { ns: 'payment' })}
            </Text>
          ) : null}
          <Text color="text.primary" fontSize={14} fontWeight={hasDiscount ? '700' : '400'} lineHeight={22}>
            {hasDiscount
              ? t(`loanDiscount.price.${userCurrency}`, { ns: 'payment' })
              : t(`price.${PlanRecurrenceEnum.yearly}.${userCurrency}`, { ns: 'payment' })}
            /{t(`period.${PlanRecurrenceEnum.yearly}`, { ns: 'payment' })}
            {!hasDiscount ? (
              <>
                {' ('}
                {t(`monthlyEquivalent.${userCurrency}`, { ns: 'payment' })}/{t('period.monthly', { ns: 'payment' })})
              </>
            ) : null}
          </Text>
        </Row>
        {hasDiscount ? (
          <Text color="text.secondary" fontSize={10} lineHeight={12}>
            {t('loanDiscount.footnote', {
              ns: 'payment',
              discountedPrice: t(`loanDiscount.price.${userCurrency}`, { ns: 'payment' }),
              renewalPrice: t(`price.${PlanRecurrenceEnum.yearly}.${userCurrency}`, { ns: 'payment' }),
            })}
          </Text>
        ) : null}
      </Column>
      <Box
        backgroundColor="surface.brand.default"
        borderRadius="radius.xs"
        paddingHorizontal="spacing.general.x2"
        paddingVertical="spacing.general.x1"
      >
        <Text color="text.inverse.primary" fontSize={12} fontWeight="500">
          {t('popular', { ns: 'payment' })}
        </Text>
      </Box>
    </Row>
  );
}

function IncludedBenefits({ onShowInsurance }: { onShowInsurance: () => void }) {
  const { t } = useTranslation('paywall');
  return (
    <Column
      backgroundColor="surface.default"
      borderRadius="radius.lg"
      gap="spacing.general.x2"
      padding="spacing.general.x4"
    >
      <Text color="text.primary" fontSize={16} fontWeight="700" lineHeight={23}>
        {t('redesign.included')}
      </Text>
      <Column gap="spacing.general.x1">
        <PaywallCheck>{t('redesign.unlimitedLending')}</PaywallCheck>
        <PaywallCheck>{t('redesign.unlimitedBorrowing')}</PaywallCheck>
      </Column>
      <Pressable onPress={onShowInsurance}>
        <Row
          alignItems="center"
          backgroundColor="surface.secondary-alt"
          borderRadius="radius.sm"
          gap="spacing.general.x2"
          padding="spacing.general.x2"
        >
          <InsuredBadge height={32} width={32} />
          <Text color="text.primary" flex={1} fontSize={16} fontWeight="700">
            {t('redesign.damageCoverage')}
          </Text>
        </Row>
      </Pressable>
      <Text color="text.secondary" fontSize={14} lineHeight={16}>
        {t('redesign.coverageFootnote')}{' '}
        <InlineText
          color="text.secondary"
          fontSize={14}
          fontWeight="700"
          onPress={onShowInsurance}
          textDecorationLine="underline"
        >
          {t('redesign.learnMore')}
        </InlineText>
      </Text>
    </Column>
  );
}

function PaywallCheck({ children }: { children: React.ReactNode }) {
  return (
    <Row
      alignItems="center"
      gap="spacing.general.x2"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
    >
      <IconCheckbox color={theme.colors['icon.primary']} size={32} strokeWidth={ICON_STROKE_WIDTH} />
      <Text color="text.primary" flex={1} fontSize={16} fontWeight="700">
        {children}
      </Text>
    </Row>
  );
}

export const BenefitsList = function BenefitsList({
  header,
  separatorAtTop,
  showCancelAnytime = true,
}: {
  header?: React.ReactElement;
  separatorAtTop?: boolean;
  showCancelAnytime?: boolean;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const useIsInsured = useUserIsInsured();
  const userCanBeInsured = useUserCanBeInsured();
  const referralPrizeAvailable = useReferralPrizeAvailable();

  const benefits = [
    'firstFeature',
    referralPrizeAvailable ? 'secondFeature' : undefined, // 1 referral = 1 free month
    userCanBeInsured || useIsInsured ? 'thirdFeature' : undefined, // insurance
    showCancelAnytime ? 'fourthFeature' : undefined, // Cancel anytime
  ].filter(Boolean);

  if (benefits.length < 2) {
    return null;
  }

  return (
    <>
      {header}
      {benefits.map((feature) => (
        <Column gap="spacing.general.x2" key={feature}>
          {separatorAtTop || feature !== 'firstFeature' ? <Separator /> : null}
          <Pressable
            onPress={() =>
              referralPrizeAvailable && feature === 'secondFeature' ? navigation.navigate('invite') : null
            }
          >
            <Row alignItems="center" gap="spacing.general.x2" marginBottom="spacing.general.x2">
              <IconCheckbox strokeWidth={ICON_STROKE_WIDTH} />
              <Text flex={1} fontWeight="700">
                {t(feature!, { ns: 'paywall' })}
              </Text>
            </Row>
          </Pressable>
        </Column>
      ))}
    </>
  );
};

export default Paywall;
